/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

const REQUEST_ID_HEADER = "X-Request-ID";

/**
 * Reads a header value from either a plain object or a Fetch/axios-style headers
 * collection, handling common request-id header name variants.
 *
 * @param {object|Headers|null|undefined} headers - Response or request headers.
 * @param {string} headerName - Header name to look up.
 * @returns {string|null} The resolved header value, if present.
 */
const getHeaderValue = (headers, headerName) => {
  if (!headers) {
    return null;
  }

  if (typeof headers.get === "function") {
    return (
      headers.get(headerName) ||
      headers.get(headerName.toLowerCase()) ||
      headers.get(headerName.toUpperCase()) ||
      null
    );
  }

  return (
    headers[headerName] ||
    headers[headerName.toLowerCase()] ||
    headers[headerName.toUpperCase()] ||
    headers.requestId ||
    headers.request_id ||
    null
  );
};

/**
 * Generates a unique request id for correlating frontend requests and backend logs.
 * Uses `crypto.randomUUID()` when available and falls back to a time-based id.
 *
 * @returns {string} A unique request id.
 */
export const generateRequestId = () => {
  if (typeof globalThis !== "undefined" && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return `req-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

/**
 * Appends the request id reference text to a message so support can trace it later.
 *
 * @param {string} message - The original error or notification message.
 * @param {string|null|undefined} requestId - The correlation id to append.
 * @returns {string} The formatted message, optionally with a reference id suffix.
 */
export const formatMessageWithReferenceId = (message, requestId) => {
  if (!requestId) {
    return message;
  }

  const baseMessage = (message || "Something went wrong")
    .replace(/\s*\(Reference ID: [^)]+\)\s*$/u, "")
    .trimEnd();
  const referenceSuffix = `(Reference ID: ${requestId})`;

  return `${baseMessage} ${referenceSuffix}`;
};

/**
 * Determines whether an error should expose a user-visible reference id.
 *
 * @param {object} error - The error object to inspect.
 * @returns {boolean} True for server-side failures that should be traceable.
 */
export const shouldExposeReferenceId = (error) => {
  const status = error?.response?.status;

  return typeof status === "number" && status >= 500;
};

/**
 * Formats a nested error payload while preserving its structure and appending
 * the reference id to any user-facing string messages.
 *
 * @param {unknown} value - Error payload value.
 * @param {string} requestId - The correlation id to append.
 * @returns {unknown} The formatted payload value.
 */
const formatErrorPayloadWithReferenceId = (value, requestId) => {
  if (typeof value === "string") {
    return formatMessageWithReferenceId(value, requestId);
  }

  if (Array.isArray(value)) {
    return value.map((item) => formatErrorPayloadWithReferenceId(item, requestId));
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => [
      key,
      formatErrorPayloadWithReferenceId(nestedValue, requestId),
    ]),
  );
};

/**
 * Returns the most useful human-readable message from a backend error payload.
 *
 * @param {unknown} payload - Response body or error payload.
 * @returns {string|null} A primary message if one can be inferred.
 */
const getPrimaryErrorMessage = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  if (typeof payload.error === "string") {
    return payload.error;
  }

  if (typeof payload.message === "string") {
    return payload.message;
  }

  if (typeof payload.detail === "string") {
    return payload.detail;
  }

  const baseErrors = payload.errors?.base;

  if (typeof baseErrors === "string") {
    return baseErrors;
  }

  if (Array.isArray(baseErrors) && typeof baseErrors[0] === "string") {
    return baseErrors[0];
  }

  return null;
};

/**
 * Creates a detached copy of an error-like object while keeping its prototype.
 *
 * @param {object} error - The error object to clone.
 * @returns {object} A cloned error object.
 */
const cloneErrorLikeObject = (error) => {
  if (!error || typeof error !== "object") {
    return error;
  }

  const clonedError =
    error instanceof Error
      ? new Error(error.message)
      : Object.create(Object.getPrototypeOf(error) || Object.prototype);

  Object.assign(clonedError, error);

  if (typeof error.message === "string") {
    clonedError.message = error.message;
  }

  if (typeof error.name === "string") {
    clonedError.name = error.name;
  }

  if (typeof error.stack === "string") {
    clonedError.stack = error.stack;
  }

  if ("cause" in error) {
    clonedError.cause = error.cause;
  }

  return clonedError;
};

/**
 * Extracts a request id from response headers, supporting the app's canonical
 * `X-Request-ID` header and its lowercase variant.
 *
 * @param {object|Headers|null|undefined} headers - Response headers.
 * @returns {string|null} The request id, if present.
 */
export const getRequestIdFromHeaders = (headers) =>
  getHeaderValue(headers, REQUEST_ID_HEADER) ||
  getHeaderValue(headers, "x-request-id");

/**
 * Resolves a request id from an axios error object so the error can be traced
 * back to the originating request or a caller-supplied fallback.
 *
 * @param {object} error - The axios or app error object.
 * @param {string|null|undefined} fallbackRequestId - Optional explicit fallback id.
 * @returns {string|null} The best available request id.
 */
export const getRequestIdFromError = (error, fallbackRequestId = null) =>
  getRequestIdFromHeaders(error?.response?.headers) ||
  error?.response?.requestId ||
  error?.requestId ||
  getRequestIdFromHeaders(error?.config?.headers) ||
  error?.config?.requestId ||
  error?.config?.metadata?.requestId ||
  fallbackRequestId ||
  null;

/**
 * Adds a stable correlation id to an axios request config so the browser request,
 * backend log entry, and any later error message can be traced together.
 *
 * @param {object} config - The outgoing axios request config.
 * @returns {object} The same config with an `X-Request-ID` header, request metadata,
 * and a reusable `requestId` field. Plain-object headers are shallow-copied; a
 * `Headers` instance is updated in place.
 */
export const attachRequestIdToConfig = (config = {}) => {
  const nextConfig = { ...config };
  const nextHeaders = nextConfig.headers || {};
  const existingRequestId =
    nextConfig.requestId ||
    nextConfig.metadata?.requestId ||
    getRequestIdFromHeaders(nextHeaders);
  const requestId = existingRequestId || generateRequestId();

  if (typeof nextHeaders.set === "function") {
    nextHeaders.set(REQUEST_ID_HEADER, requestId);
    nextConfig.headers = nextHeaders;
  } else {
    nextConfig.headers = {
      ...nextHeaders,
      [REQUEST_ID_HEADER]: requestId,
    };
  }

  nextConfig.metadata = {
    ...nextConfig.metadata,
    requestId,
  };
  nextConfig.requestId = requestId;

  return nextConfig;
};

/**
 * Enriches an error object with the best available request id and appends the
 * reference id to user-facing error text where possible.
 *
 * @param {object} error - The error object to enrich.
 * @param {string|null|undefined} fallbackRequestId - Optional explicit fallback id.
 * @returns {object} The same error object with correlation metadata attached.
 */
export const attachReferenceIdToError = (error, fallbackRequestId = null) => {
  const requestId = getRequestIdFromError(error, fallbackRequestId);

  if (!requestId || !shouldExposeReferenceId(error)) {
    return error;
  }

  const enrichedError = cloneErrorLikeObject(error);
  enrichedError.requestId = requestId;
  enrichedError.referenceId = requestId;

  if (typeof enrichedError?.message === "string") {
    enrichedError.message = formatMessageWithReferenceId(
      enrichedError.message,
      requestId,
    );
  }

  if (typeof enrichedError?.response?.data === "string") {
    enrichedError.response = {
      ...enrichedError.response,
      data: formatMessageWithReferenceId(enrichedError.response.data, requestId),
    };
  } else if (enrichedError?.response?.data && typeof enrichedError.response.data === "object") {
    const formattedResponseData = formatErrorPayloadWithReferenceId(
      enrichedError.response.data,
      requestId,
    );

    formattedResponseData.referenceId = requestId;

    const primaryErrorMessage = getPrimaryErrorMessage(formattedResponseData);

    if (primaryErrorMessage && typeof formattedResponseData.error !== "string") {
      formattedResponseData.error = formatMessageWithReferenceId(
        primaryErrorMessage,
        requestId,
      );
    }

    enrichedError.response = {
      ...enrichedError.response,
      data: formattedResponseData,
    };
  }

  return enrichedError;
};
